import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Activity, AlertTriangle, ArrowLeft, Archive, BarChart3, ClipboardCheck, FileText, Layers, Link2, MessageSquare, Save, Pencil, Trash2, X, Settings2, Clock, Calendar, Users, Loader2, GitBranch, Maximize2 } from "lucide-react";
import { TopBar } from "@/components/layout/TopBar";
import { TeamCard } from "@/components/capacity/TeamCard";
import { CommentsButton } from "@/components/project/CommentsButton";
import { ConcernsCard } from "@/components/project/ConcernsCard";
import { Drawer } from "@/components/ui/Drawer";
import { LatestPulseLine } from "@/components/shared/LatestPulseLine";
import { RichText } from "@/components/ui/RichText";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { OverviewHero } from "@/components/project/OverviewLanding";
import { QuickMilestones } from "@/components/project/QuickMilestones";
import { ProjectDetailsCard } from "@/components/project/ProjectDetailsCard";
import { CommunicationPlanPanel } from "@/components/shared/CommunicationPlanPanel";
import { PlanPanel } from "@/components/project/PlanPanel";
import { PulseJournal } from "@/components/project/PulseJournal";
import { FreshserviceAttachmentsCard } from "@/components/project/FreshserviceAttachmentsCard";
import { BriefPanel } from "@/components/project/BriefPanel";
import { JiraPanel } from "@/components/jira/JiraPanel";
import { PinnedLinksStrip, ResourcesPanel } from "@/components/shared/ResourcesPanel";
import {
  useProjectLinks,
  useAddProjectLink,
  useUpdateProjectLink,
  useDeleteProjectLink,
} from "@/hooks/useLinks";
import { cn } from "@/lib/cn";
import { useCan } from "@/lib/roles";
import { avatarTone, formatDate } from "@/lib/format";
import { toast } from "sonner";
import api from "@/lib/api";
import { useProject, useUpdateProject, useDeleteProject, useArchiveProject, useUnarchiveProject, useProjectChildren, usePortfolio } from "@/hooks/usePortfolio";
import { statusPillClass, statusShortLabel, PRE_START_STATUSES } from "@/lib/projectStatus";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Role/phase constant tables removed 2026-09-14 with the Capacity tab
// (old derived model: role %% of est_hours split across phase weights).

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Where this project was opened from. Set by the calling page via
  // navigate(..., { state: { from } }). Used to send the user back to
  // their starting context on Back, Archive, or Delete instead of
  // dumping them on /portfolio. Falls back to /portfolio when no source
  // is set (deep link, fresh tab, refresh).
  const fromPath = (location.state as { from?: string } | null)?.from ?? "/portfolio";
  const fromLabel = (() => {
    if (fromPath.startsWith("/executive")) return "Executive";
    if (fromPath.startsWith("/teams/")) return "Team";
    if (fromPath.startsWith("/portfolio")) return "Portfolio";
    return "Back";
  })();
  const project = useProject(projectId ?? "");
  const deleteProjectMut = useDeleteProject();
  const archiveProjectMut = useArchiveProject();
  const unarchiveProjectMut = useUnarchiveProject();
  const [confirmAction, setConfirmAction] = useState<null | "archive" | "delete">(null);

  // Tab state — URL-driven so the peek drawer can deep-link straight
  // into Brief / Plan / Jira / Resources, and refresh restores the
  // active tab. Falls back to "overview" when no `?tab=` is present.
  type DetailTab = "overview" | "plan" | "comm-plan" | "brief" | "jira" | "resources";
  const VALID_DETAIL_TABS: ReadonlySet<DetailTab> = new Set([
    "overview", "plan", "comm-plan", "brief", "jira", "resources",
  ]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialTab: DetailTab =
    tabParam && (VALID_DETAIL_TABS as Set<string>).has(tabParam) ? (tabParam as DetailTab) : "overview";
  const [tab, setTabState] = useState<DetailTab>(initialTab);
  const setTab = (next: DetailTab) => {
    setTabState(next);
    const params = new URLSearchParams(searchParams);
    if (next === "overview") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params, { replace: true });
  };
  // Sync state if URL changes (browser back/forward across tabs).
  useEffect(() => {
    if (tabParam && (VALID_DETAIL_TABS as Set<string>).has(tabParam) && tabParam !== tab) {
      setTabState(tabParam as DetailTab);
    } else if (!tabParam && tab !== "overview") {
      setTabState("overview");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);

  // Resource Links — used by Overview pinned strip + Resources tab
  const linksQ = useProjectLinks(projectId ?? null);
  const addLink = useAddProjectLink(projectId ?? "");
  const updateLink = useUpdateProjectLink(projectId ?? "");
  const deleteLink = useDeleteProjectLink(projectId ?? "");

  // Right-side drawer state (Status Pulse + RAID Log).
  const [pulseDrawerOpen, setPulseDrawerOpen] = useState(false);
  const [raidDrawerOpen, setRaidDrawerOpen] = useState(false);

  const p = project.data;
  const estHours = p?.est_hours ?? 0;
  const durationWeeks = p?.start_date && p?.end_date
    ? Math.max(1, (new Date(p.end_date).getTime() - new Date(p.start_date).getTime()) / (7 * 86400000))
    : 7;

  if (project.isLoading) {
    return (
      <>
        <TopBar title="Project Detail" />
        <div className="p-8">
          <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">
            Loading project...
          </div>
        </div>
      </>
    );
  }

  if (!p) {
    return (
      <>
        <TopBar title="Project Not Found" />
        <div className="p-8">
          <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-6 text-sm text-red-800 dark:text-red-300">
            Project {projectId} not found.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* TopBar shows just the ID + size meta — the big page title lives
          in the hero below so we don't duplicate it. The TopBar's job
          here is navigation chrome (back button, actions) + breadcrumb. */}
      <TopBar
        title={p.id}
        subtitle={`${estHours}h est. · ${Math.round(durationWeeks)}wk`}
      >
        <button
          onClick={() => navigate(fromPath)}
          className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-3 hover:bg-raised transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {fromLabel}
        </button>
        {/* Comments — primary action. Indigo-filled because it's the
            most-used surface (daily reads, frequent writes). All other
            actions are secondary chrome compared to this. */}
        {projectId && (
          <CommentsButton
            entityId={projectId}
            title="Project comments"
            fsTicketId={p.source === "freshservice" ? p.external_id : null}
          />
        )}
        {/* Unarchive is its own affirmative action when the project is
            archived — keep it visible (emerald) so the path back from
            archived is obvious. Archive (the inverse) is rare; lives in
            the actions menu below. */}
        {p.archived_at && (
          <button
            onClick={async () => {
              if (!projectId) return;
              await unarchiveProjectMut.mutateAsync(projectId);
              toast.success(`${p.name} restored`);
            }}
            disabled={unarchiveProjectMut.isPending}
            className="flex items-center gap-1.5 rounded-lg border border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 transition-colors disabled:opacity-50"
            title="Restore this project to active views"
          >
            <Archive className="h-3.5 w-3.5" />
            {unarchiveProjectMut.isPending ? "Restoring..." : "Unarchive"}
          </button>
        )}
        {/* Actions menu — Archive and Delete consolidated here so
            destructive actions don't sit at the same visual weight
            as Comments (the most-frequent action). Delete in
            particular was identical-styled to Portfolio nav, which
            is bad signaling for an irreversible op. */}
        <ProjectActionsMenu
          archived={!!p.archived_at}
          onArchive={() => setConfirmAction("archive")}
          onDelete={() => setConfirmAction("delete")}
          archiving={archiveProjectMut.isPending}
          deleting={deleteProjectMut.isPending}
        />
      </TopBar>

      {/* Confirm modal — same shape as Portfolio's delete dialog so the
          confirmation feel is consistent across the planner. */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-sm rounded-xl bg-surface p-5 shadow-xl">
            <h3 className="text-sm font-semibold text-ink">
              {confirmAction === "archive" ? "Archive this project?" : "Delete this project?"}
            </h3>
            <p className="mt-2 text-xs text-ink-3">
              {confirmAction === "archive" ? (
                <>
                  <strong>{p.name}</strong> will be hidden from every active view —
                  Portfolio tabs, the Executive page, capacity, and initiative rollups.
                  The record stays in the database and can be restored from the
                  <strong> Archived </strong> tab.
                </>
              ) : (
                <>
                  <strong>{p.name}</strong> will be permanently removed along with its
                  assignments, comments, links, and RAID entries. This cannot be undone.
                </>
              )}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setConfirmAction(null)}
                className="rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-3 hover:bg-raised"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!projectId) return;
                  if (confirmAction === "archive") {
                    await archiveProjectMut.mutateAsync(projectId);
                    toast.success(`${p.name} archived`);
                    setConfirmAction(null);
                    navigate(fromPath);
                  } else {
                    await deleteProjectMut.mutateAsync(projectId);
                    toast.success(`${p.name} deleted`);
                    setConfirmAction(null);
                    navigate(fromPath);
                  }
                }}
                disabled={archiveProjectMut.isPending || deleteProjectMut.isPending}
                className={
                  confirmAction === "delete"
                    ? "rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    : "rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                }
              >
                {confirmAction === "delete"
                  ? deleteProjectMut.isPending ? "Deleting..." : "Delete"
                  : archiveProjectMut.isPending ? "Archiving..." : "Archive"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs + drawer triggers. Status Pulse and RAID Log are
          right-side drawers, not tab panes — they're running logs,
          one-click reachable but not occupying Overview real estate. */}
      <div className="border-b border-line bg-surface px-8">
        <nav className="flex gap-1">
          {([
            { key: "overview" as const, label: "Overview", icon: FileText },
            { key: "brief" as const, label: "Brief", icon: ClipboardCheck },
            { key: "jira" as const, label: "Jira", icon: GitBranch },
            { key: "resources" as const, label: "Resources", icon: Link2 },
            { key: "plan" as const, label: "Plan", icon: Calendar },
            { key: "comm-plan" as const, label: "Communication Plan", icon: MessageSquare },
          ]).map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={cn(
                "flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors",
                tab === key
                  ? "border-brand-navy-600 text-brand-navy-700 dark:text-brand-navy-300"
                  : "border-transparent text-ink-muted hover:text-ink-2 hover:border-line-strong",
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
          {/* Status Pulse nav button removed — the hero footer's
              "Status Pulse · …" line is the single entry point now. */}
          <button
            onClick={() => setRaidDrawerOpen(true)}
            className="flex items-center gap-2 border-b-2 border-transparent px-4 py-3 text-sm font-medium text-ink-muted transition-colors hover:text-ink-2 hover:border-line-strong"
          >
            <AlertTriangle className="h-4 w-4" />
            RAID
          </button>
        </nav>
      </div>

      <div className="space-y-8 p-8">
        {/* ============================================================ */}
        {/* TAB: Overview — Project Info + Assignments + Comments         */}
        {/* ============================================================ */}
        {tab === "overview" && p && <>
        {/* Hero — single identity block. Title, description, state line
            (health/status/priority/dates), org line (PM/sponsor/team/
            phase), progress, schedule signal. Edit affordance opens
            EditProjectDialog for the full attribute set (BA, t-shirt,
            initiative, spec dates). The previous AttributesCard and
            chip strip were removed — they duplicated hero fields and
            the tab bar respectively, and pushed the narrative below
            the fold. */}
        <OverviewHero
          project={p}
          pinnedLinks={
            (linksQ.data?.some((l) => l.pinned) ?? false) ? (
              <PinnedLinksStrip
                links={linksQ.data}
                onSeeAll={() => setTab("resources")}
              />
            ) : undefined
          }
          latestPulse={
            // Always render — LatestPulseLine handles the empty-state
            // CTA itself ("No update yet · + Add →"), so a project
            // with no pulse text still has a discoverable entry point
            // into the drawer. Was previously conditional on
            // p.status_update being truthy, which caused new projects
            // to lose Status Pulse access entirely.
            <LatestPulseLine
              text={p.status_update}
              onSeeAll={() => setPulseDrawerOpen(true)}
            />
          }
        />

        {/* Quick Milestones (Brett, 2026-09-14) — lightweight reporting
            milestones + go-to-green plans; the backbone of the weekly
            C-level updates. Same project_milestones rows as the Plan
            tab — this card just edits the reporting subset. */}
        {projectId && (
          <div className="rounded-xl border border-line bg-surface px-6 py-4">
            <QuickMilestones projectId={projectId} />
          </div>
        )}

        {/* Parent/child strip — only renders when the project is part
            of a program. Shows 'Parent: <name>' when this row is a
            child, or 'Children (N)' with the list when it's a parent. */}
        {projectId && (p.parent_project_id || (p.child_count ?? 0) > 0) && (
          <ProjectParentChildStrip
            projectId={projectId}
            parentId={p.parent_project_id}
            childCount={p.child_count ?? 0}
          />
        )}

        {/* In-body Status Pulse retired — content lives in the
            right-side drawer triggered from the nav bar and from the
            hero's "Latest" line. */}

        {/* Project Details + Team — stacked full-width now that the
            Team card is a wider table (roster + capacity in one).
            Matches the Initiative Detail layout. */}
        <div className="space-y-4">
          <ProjectDetailsCard project={p} />
          {projectId && (
            <TeamCard
              entityKind="project"
              entityId={projectId}
              // Show the "earliest start" hint only when the project
              // is in a pre-start state — once work is underway the
              // suggested-start math is misleading. Terminal statuses
              // (complete/postponed/declined) likewise skip the hint.
              scheduleHintProjectId={
                (PRE_START_STATUSES as string[]).includes(p.status ?? "")
                  ? projectId
                  : null
              }
            />
          )}
        </div>

        {/* Details — markdown body. The substance/scope of the project,
            distinct from the one-line description in the hero and from
            the formal Brief (which freezes after intake). For projects
            without a paired brief, this is the de facto "what is this
            thing" doc. */}
        {projectId && (
          <DetailsCard
            projectId={projectId}
            value={p.details}
            placeholder="What we're delivering, scope, sub-items, requirements..."
          />
        )}

        {/* Linked Jira status moved INTO the hero (next to progress +
            slip) — Jira completion is a status signal, not its own
            concern. The old standalone strip is gone; the inline
            version lives in OverviewLanding.tsx :: JiraInlineStatus. */}

        {/* RAID log retired from the Overview body — lives in the
            right-side drawer triggered from the nav bar. */}

        {/* Team Assignments moved up into the identity row next to
            Project Details (Option A layout, consistent with the
            Initiative Detail page). */}

        {/* Comments live in the drawer opened by the TopBar
            CommentsButton — same pattern as Jira issues. No bottom-of-
            page panel anymore. */}
        </>}

        {/* ============================================================ */}
        {/* TAB: Brief — Kickstart Brief (intake form for exec approval)  */}
        {/* ============================================================ */}
        {tab === "brief" && projectId && (
          <BriefPanel projectId={projectId} project={project.data ?? null} />
        )}

        {/* ============================================================ */}
        {/* TAB: Jira — linked Atlassian issues + status rollup           */}
        {/* ============================================================ */}
        {tab === "jira" && projectId && (
          <JiraPanel projectId={projectId} />
        )}

        {/* ============================================================ */}
        {/* TAB: Resources — links to spreadsheets, folders, dashboards   */}
        {/* ============================================================ */}
        {tab === "resources" && projectId && (
          <div className="space-y-4">
            {/* Files from Freshservice — mirrors attachments on the
                source ticket (kickstart briefs, supporting docs). Lives
                in Resources alongside the typed-in links so all project
                documents share one home. FS-sourced projects only. */}
            {p.source === "freshservice" && p.external_id && (
              <FreshserviceAttachmentsCard projectId={projectId} />
            )}
            <ResourcesPanel
              entityLabel="project"
              links={linksQ.data}
              isLoading={linksQ.isLoading}
              onAdd={(payload) => addLink.mutateAsync(payload)}
              onUpdate={(patch) => updateLink.mutateAsync(patch)}
              onDelete={(linkId) => deleteLink.mutateAsync(linkId)}
            />
          </div>
        )}

        {/* ============================================================ */}
        {/* TAB: Capacity — Role Config + Phase Plan + Resources + Math   */}
        {/* ============================================================ */}
        {/* ============================================================ */}
        {/* TAB: Plan — Milestones + Tasks                              */}
        {/* ============================================================ */}
        {tab === "plan" && projectId && (
          <PlanPanel projectId={projectId} />
        )}

        {/* ============================================================ */}
        {/* TAB: Communication Plan — stakeholder × deliverable matrix    */}
        {/* ============================================================ */}
        {tab === "comm-plan" && projectId && (
          <CommunicationPlanPanel scope="project" scopeId={projectId} />
        )}
      </div>

      {/* Status Pulse drawer — the dated journal (entries auto-stamp
          date + author, @mentions from the roster). Replaced the
          single-blob StatusPulseCard editor 2026-09-03. */}
      <Drawer
        open={pulseDrawerOpen}
        onClose={() => setPulseDrawerOpen(false)}
        title="Status Pulse"
        subtitle={p ? `${p.id} · ${p.name}` : undefined}
      >
        {projectId && p && <PulseJournal projectId={projectId} />}
      </Drawer>

      {/* RAID Log drawer — risks / issues / decisions / assumptions. */}
      <Drawer
        open={raidDrawerOpen}
        onClose={() => setRaidDrawerOpen(false)}
        title="RAID Log"
        subtitle={p ? `${p.id} · ${p.name}` : undefined}
      >
        {projectId && <ConcernsCard projectId={projectId} />}
      </Drawer>
    </>
  );
}

// ---------------------------------------------------------------------------
// Project Resources Card
// ---------------------------------------------------------------------------

function ProjectActionsMenu({
  archived,
  onArchive,
  onDelete,
  archiving,
  deleting,
}: {
  archived: boolean;
  onArchive: () => void;
  onDelete: () => void;
  archiving: boolean;
  deleting: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  // Delete is a PMO action (2026-09-28); Archive stays a User action.
  const { isPmo } = useCan();

  // Click-outside to close. Esc handled by the menu itself.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-line bg-surface text-ink-muted hover:bg-raised hover:text-ink-2 transition-colors"
        title="More actions"
        aria-label="More actions"
      >
        <span className="text-base leading-none">⋯</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-44 overflow-hidden rounded-lg border border-line bg-surface shadow-lg">
          {/* Archive — only when not already archived. Quiet style;
              not destructive but irreversible-ish. Unarchive lives
              outside the menu as an affirmative emerald button. */}
          {!archived && (
            <button
              onClick={() => {
                setOpen(false);
                onArchive();
              }}
              disabled={archiving}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-ink-2 hover:bg-raised disabled:opacity-50"
            >
              <Archive className="h-3.5 w-3.5 text-ink-muted" />
              {archiving ? "Archiving…" : "Archive"}
            </button>
          )}
          {/* Delete — clearly destructive. Red text on hover, sits at
              the bottom under a divider so it's separated visually from
              the safer Archive option. */}
          {isPmo && (
          <button
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
            disabled={deleting}
            data-testid="project-delete"
            className="flex w-full items-center gap-2 border-t border-line-soft px-3 py-2 text-left text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/15 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {deleting ? "Deleting…" : "Delete"}
          </button>
          )}
        </div>
      )}
    </div>
  );
}

function FreshnessLine({ iso, stale }: { iso: string | null | undefined; stale: boolean }) {
  const days = daysSinceIso(iso);
  if (days == null) {
    return <span className="text-xs text-ink-subtle">never</span>;
  }
  const phrase = days === 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`;
  if (stale) {
    return (
      <span className="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-300">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
        {phrase} — may be out of sync
      </span>
    );
  }
  return <span className="text-ink-3">{phrase}</span>;
}

// ---------------------------------------------------------------------------
// DetailsCard — markdown body that holds the substance of the project.
// Lives between the hero and the operational cards (Status Pulse, RAID).
//
// Distinct from:
//   - Description (one-liner in the hero — for orientation)
//   - Brief         (committee-approval doc — freezes after intake)
//   - Status Pulse  (this-week narrative — short, present-tense)
//   - Comments      (chronological thread — discussion)
//
// This is the doc-level "what we're delivering, scope, sub-items,
// requirements" — the place that's been missing from Project Detail.
// ---------------------------------------------------------------------------

function DetailsCard({
  projectId,
  value,
  placeholder,
}: {
  projectId: string;
  value: string | null;
  placeholder?: string;
}) {
  const updateProject = useUpdateProject();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");

  // Re-sync draft if the underlying field changes (another tab saved).
  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  const hasContent = (value ?? "").trim().length > 0;

  const handleSave = async () => {
    const next = draft.trim();
    if (next === (value ?? "").trim()) {
      setEditing(false);
      return;
    }
    try {
      await updateProject.mutateAsync({ id: projectId, details: next } as any);
      setEditing(false);
      toast.success(next ? "Details updated." : "Details cleared.");
    } catch {
      toast.error("Failed to save details.");
    }
  };

  const handleCancel = () => {
    setDraft(value ?? "");
    setEditing(false);
  };

  return (
    <div className="rounded-xl border border-line bg-surface p-6">
      <div className="mb-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-brand-navy-500" />
          <h2 className="text-sm font-semibold text-ink-2">Details</h2>
        </div>
        {editing ? (
          <div className="flex gap-1.5">
            <button
              onClick={handleCancel}
              className="flex items-center gap-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-[11px] font-medium text-ink-3 hover:bg-raised transition-colors"
            >
              <X className="h-3 w-3" /> Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={updateProject.isPending}
              className="flex items-center gap-1 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-brand-navy-700 transition-colors disabled:opacity-50"
            >
              {updateProject.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Save className="h-3 w-3" />
              )}
              {updateProject.isPending ? "Saving..." : "Save"}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-[11px] font-medium text-ink-3 hover:bg-raised transition-colors"
          >
            <Pencil className="h-3 w-3" /> {hasContent ? "Edit" : "Add details"}
          </button>
        )}
      </div>

      {editing ? (
        <RichTextEditor
          value={draft}
          onChange={setDraft}
          placeholder={placeholder ?? "Add details..."}
          rows={10}
          autoFocus
        />
      ) : hasContent ? (
        <RichText text={value ?? ""} />
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="block w-full rounded-lg border border-dashed border-line-strong bg-canvas/40 px-4 py-6 text-left text-sm text-ink-muted hover:border-line-strong hover:bg-raised transition-colors"
        >
          <span className="italic">{placeholder ?? "No details yet — click to add."}</span>
        </button>
      )}
    </div>
  );
}


function daysSinceIso(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z").getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

function ProjectParentChildStrip({
  projectId,
  parentId,
  childCount,
}: {
  projectId: string;
  parentId: string | null | undefined;
  childCount: number;
}) {
  const navigate = useNavigate();
  const portfolioQ = usePortfolio();
  const childrenQ = useProjectChildren(childCount > 0 ? projectId : null);

  // Look up the parent's display name from the portfolio cache so the
  // chip can read 'Part of: ETE-180 — Where's My Transmission'.
  const parent = useMemo(() => {
    if (!parentId) return null;
    return (portfolioQ.data ?? []).find((p) => p.id === parentId) ?? null;
  }, [portfolioQ.data, parentId]);

  // CHILD face — slim chip with one-click back to parent.
  if (parentId) {
    return (
      <button
        onClick={() => navigate(`/portfolio/${parentId}`)}
        className="flex w-full items-center gap-2 rounded-lg border border-brand-navy-200 dark:border-brand-navy-500/30 bg-brand-navy-50/60 dark:bg-brand-navy-500/10 px-4 py-2 text-left text-xs text-brand-navy-800 dark:text-brand-navy-300 hover:bg-brand-navy-100 transition-colors"
        title="Jump to parent program"
      >
        <Layers className="h-3.5 w-3.5 text-brand-navy-500" />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-navy-600 dark:text-brand-navy-400">
          Part of
        </span>
        <span className="font-mono text-[11px] text-brand-navy-700 dark:text-brand-navy-300">{parentId}</span>
        {parent && <span className="truncate font-medium">{parent.name}</span>}
        <span className="ml-auto text-[10px] text-brand-navy-500">View parent →</span>
      </button>
    );
  }

  // PARENT face — header + children list.
  if (childCount > 0) {
    const children = childrenQ.data ?? [];
    return (
      <div className="rounded-xl border border-brand-navy-200 dark:border-brand-navy-500/30 bg-brand-navy-50/40 dark:bg-brand-navy-500/10 p-4">
        <div className="mb-3 flex items-center gap-2">
          <Layers className="h-4 w-4 text-brand-navy-500" />
          <h2 className="text-sm font-semibold text-brand-navy-900 dark:text-brand-navy-300">
            Program with {childCount} child project{childCount === 1 ? "" : "s"}
          </h2>
        </div>
        {childrenQ.isLoading ? (
          <div className="text-xs text-ink-muted">Loading children...</div>
        ) : children.length === 0 ? (
          <div className="text-xs text-ink-muted italic">No children yet.</div>
        ) : (
          <div className="space-y-1.5">
            {children.map((c) => {
              const pct = Math.round((c.pct_complete ?? 0) * 100);
              return (
                <button
                  key={c.id}
                  onClick={() => navigate(`/portfolio/${c.id}`)}
                  className="flex w-full items-center gap-3 rounded-md border border-line bg-surface px-3 py-2 text-left text-xs hover:border-brand-navy-300 hover:bg-brand-navy-50 dark:hover:bg-brand-navy-500/15 transition-colors"
                >
                  <span className="font-mono text-[10px] text-ink-muted shrink-0">{c.id}</span>
                  <span className="truncate font-medium text-ink flex-1">{c.name}</span>
                  {c.status && (
                    <span className={cn("rounded-full px-1.5 py-0.5 text-[9px] font-semibold shrink-0", statusPillClass(c.status))}>
                      {statusShortLabel(c.status)}
                    </span>
                  )}
                  <span className="w-12 tabular-nums text-right text-[10px] text-ink-3 shrink-0">
                    {pct}%
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return null;
}
